import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useYouTubeAccountReset } from '@/lib/youtubeConnection';
import { formatApiError } from '@/lib/formatApiError';
import { formatCount } from '@/lib/channel';
import { ChannelSectionNav } from '@/components/channel/ChannelSectionNav';
import { VideoThumb } from '@/components/channel/VideoThumb';

export function ChannelCompetitorsPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [peers, setPeers] = useState(null);
  const [analyses, setAnalyses] = useState([]);

  const loadAnalyses = useCallback(async () => {
    try {
      const { data } = await api.get('/channel/competitors/analyses');
      setAnalyses(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setAnalyses([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/channel/competitors');
      setPayload(data);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not load competitors'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadAnalyses();
    api.get('/channel/peers')
      .then(({ data }) => setPeers(data))
      .catch(() => setPeers(null));
  }, [load, loadAnalyses]);

  const clearChannel = useCallback(() => {
    setPayload({ competitors: [], gaps: [], discovery: null, connected: false });
    setPeers(null);
    setAnalyses([]);
  }, []);
  useYouTubeAccountReset(clearChannel);

  const analysisPending = analyses.some((row) => row?.status === 'pending' || row?.status === 'running');
  useEffect(() => {
    if (!analysisPending) return undefined;
    const timer = setInterval(() => {
      loadAnalyses();
    }, 2000);
    return () => clearInterval(timer);
  }, [analysisPending, loadAnalyses]);

  const add = async (event) => {
    event.preventDefault();
    const phrase = query.trim();
    if (phrase.length < 2) return;
    setAdding(true);
    try {
      await api.post('/channel/competitors', { query: phrase });
      setQuery('');
      await load();
    } catch (err) {
      toast.error(formatApiError(err, 'Could not add that channel'));
    } finally {
      setAdding(false);
    }
  };

  const remove = async (channelId) => {
    try {
      await api.delete(`/channel/competitors/${channelId}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err, 'Could not remove that channel'));
    }
  };

  const competitors = payload?.competitors || [];
  const discovery = payload?.discovery;
  const readyAnalysis = analyses.find((row) => row?.status === 'ready' && row.pack) || null;
  const shown = discovery || readyAnalysis?.pack || null;

  return (
    <main className="p-4 sm:p-8 space-y-4 font-jakarta overflow-x-hidden">
      <div className="space-y-2">
        <h1 className="text-lg font-bold text-white">Competitors</h1>
        <p className="text-xs text-[#7d8799] max-w-2xl">
          Add a channel by URL, @handle, or id. Twinnee compares recent upload pace and median views with yours.
        </p>
        <ChannelSectionNav />
      </div>
      {peers?.paid ? (
        <section className="rounded-[16px] border border-[#214f48] bg-[#13322d] p-4 max-w-2xl space-y-1">
          <h2 className="text-sm font-bold text-white">Strategy</h2>
          {peers.strategy ? <p className="text-sm text-[#d7f8ec]">{peers.strategy}</p> : null}
          <p className="text-[11px] text-[#8b95a7]">Tracking {(peers.channels || []).length} channels like yours.</p>
        </section>
      ) : null}
      {!payload?.gaps_locked && (shown || analysisPending || analyses.length) ? (
        <section className="rounded-[16px] border border-[#214f48] bg-[#13322d] p-4 max-w-2xl space-y-3" data-testid="competitor-discovery">
          <h2 className="text-sm font-bold text-white">Discovery from your uploads</h2>
          {analysisPending ? <p className="text-xs text-[#8b95a7]">Queued — comparing this upload with outlier videos.</p> : null}
          {analyses.length ? (
            <ul className="space-y-1">
              {analyses.slice(0, 5).map((row) => (
                <li key={row.id} className="text-[11px] text-[#c5cedb] flex justify-between gap-3">
                  <span className="truncate">{row.title || row.youtube_video_id || 'Upload'}</span>
                  <span className="shrink-0 text-[#8b95a7]">{row.status}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {shown?.hook_framework?.pattern ? (
            <p className="text-sm text-[#d7f8ec]">Hook: {shown.hook_framework.pattern}</p>
          ) : null}
          {(shown?.title_patterns || []).length ? (
            <p className="text-xs text-[#c5cedb]">
              Title patterns: {shown.title_patterns.map((row) => row.formula).filter(Boolean).join(', ')}
            </p>
          ) : null}
          {(shown?.primary_keywords || []).length ? (
            <p className="text-xs text-[#c5cedb]">Keywords: {shown.primary_keywords.join(', ')}</p>
          ) : null}
          {(shown?.long_tail_tags || []).length ? (
            <p className="text-xs text-[#8b95a7]">Long-tail: {shown.long_tail_tags.join(', ')}</p>
          ) : null}
          {(shown?.content_gaps || []).length ? (
            <ul className="text-xs text-[#c5cedb] space-y-1">
              {shown.content_gaps.map((gap) => (
                <li key={gap.topic}>Gap: {gap.topic}</li>
              ))}
            </ul>
          ) : null}
          <ul className="space-y-2">
            {(shown?.evidence || []).map((video) => (
              <li key={video.video_id} className="flex items-center gap-2 text-[11px] text-[#c5cedb]">
                <VideoThumb video={video} className="h-8 w-12" />
                <span className="truncate">{video.title}</span>
                <span className="shrink-0 text-[#8b95a7]">
                  {Number(video.outlier_score || 0).toFixed(1)}× · {Math.round(video.views_per_hour || 0)} vph
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <div className="grid grid-cols-2 gap-2 max-w-md">
        <div className="rounded-[14px] border border-[#1f2635] bg-[#12151d] px-3 py-2">
          <p className="text-[10px] uppercase tracking-[0.12em] text-[#7d8799]">Your median recent views</p>
          <p className="text-lg font-bold text-white">{formatCount(payload?.own_median_recent_views)}</p>
        </div>
        <div className="rounded-[14px] border border-[#1f2635] bg-[#12151d] px-3 py-2">
          <p className="text-[10px] uppercase tracking-[0.12em] text-[#7d8799]">Your uploads, 28 days</p>
          <p className="text-lg font-bold text-white">{formatCount(payload?.own_uploads_28d)}</p>
        </div>
      </div>
      {payload?.gaps_locked ? (
        <section className="rounded-[16px] border border-amber-300/30 bg-[#1a160d] p-4 space-y-2 max-w-xl">
          <p className="text-sm font-semibold text-white">Topics they posted that you have not</p>
          <p className="text-sm text-amber-100/80">Each step stays locked until a plan payment is completed.</p>
          <button
            type="button"
            onClick={() => navigate('/pricing?unlock=analytics')}
            className="inline-flex items-center gap-2 rounded-full bg-amber-300 px-4 py-2 text-sm font-bold text-[#1a1204]"
          >
            <Lock className="w-4 h-4" />
            Unlock
          </button>
        </section>
      ) : payload ? (
        <section className="space-y-2 max-w-2xl">
          <h2 className="text-sm font-bold text-white">Topics they posted that you have not</h2>
          <p className="text-xs text-[#7d8799]">Ideas from channels you added. Open one to make that video.</p>
          {(payload?.gaps || []).length === 0 ? (
            <p className="text-xs text-[#8b95a7]">
              {competitors.length
                ? 'Nothing new. You already post these topics.'
                : 'Add a channel you watch. Ideas from their videos show up here.'}
            </p>
          ) : (
            <ul className="space-y-2">
              {(payload?.gaps || []).map((gap) => (
                <li key={gap.idea_id || gap.phrase}>
                  <button
                    type="button"
                    onClick={() => navigate(`/channel/next/${encodeURIComponent(gap.idea_id)}`)}
                    className="w-full text-left rounded-[16px] border border-[#1f2635] bg-[#12151d] p-3 hover:border-[#34d399]/40"
                  >
                    <span className="block text-sm font-semibold text-white">{gap.phrase}</span>
                    <span className="block text-[11px] text-[#8b95a7] mt-1">
                      Usual views {formatCount(gap.median_views)}
                      {gap.posted_recently ? ' · Posted in the last 28 days' : ''}
                    </span>
                    <ul className="mt-2 space-y-1">
                      {(gap.evidence || []).map((video) => (
                        <li key={`${gap.phrase}-${video.video_id || video.title}`} className="text-[11px] text-[#c5cedb] truncate">
                          {video.channel_title}: {video.title} · {formatCount(video.view_count)} views
                        </li>
                      ))}
                    </ul>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}
      <form onSubmit={add} className="flex gap-2">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="@channel, URL, or channel id"
          className="h-10 flex-1 max-w-md px-3 rounded-full bg-[#12151d] border border-[#1f2635] text-sm text-white"
        />
        <button type="submit" disabled={adding} className="h-10 px-4 rounded-full bg-[#34d399] text-[#06211b] text-xs font-bold inline-flex items-center gap-1.5">
          {adding && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Add
        </button>
      </form>
      {loading ? <p className="text-xs text-[#7d8799]">Loading…</p> : null}
      <div className="space-y-3">
        {competitors.map((channel) => (
          <article key={channel.channel_id} className="rounded-[16px] border border-[#1f2635] bg-[#12151d] p-4 space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-sm font-bold text-white">{channel.title}</h2>
                <p className="text-[11px] text-[#8b95a7]">
                  {formatCount(channel.subscriber_count)} subscribers · median recent views {formatCount(channel.median_recent_views)} · {channel.uploads_28d} uploads in sample, 28 days
                </p>
                <p className="text-[11px] text-[#c5cedb]">
                  {channel.views_vs_you >= 0 ? `${formatCount(channel.views_vs_you)} more median views than you` : `${formatCount(Math.abs(channel.views_vs_you))} fewer median views than you`}
                </p>
              </div>
              <button type="button" onClick={() => remove(channel.channel_id)} className="text-[11px] text-[#8b95a7] hover:text-white">
                Remove
              </button>
            </div>
            <ul className="text-[11px] text-[#c5cedb] space-y-1">
              {(channel.recent_videos || []).slice(0, 5).map((video) => (
                <li key={video.video_id} className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2 min-w-0">
                    <VideoThumb video={video} className="h-8 w-12" />
                    <span className="truncate">{video.title}</span>
                  </span>
                  <span className="shrink-0 text-[#8b95a7]">
                    {formatCount(video.view_count)}
                    {video.outlier_score ? ` · ${Number(video.outlier_score).toFixed(1)}×` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </main>
  );
}

export default ChannelCompetitorsPage;
